import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useScroll } from "../../contexts/useScroll";
import { Message, User } from "../../core/types";
import { PageContext, PageOptions } from "../../types/page";
import ChatMessageList from "./components/ChatMessageList";
import ChatFormText from "./components/ChatFormText";
import ArrowLeftIcon from "../../components/icons/ArrowLeftIcon";
import ChatFormVoice from "./components/ChatFormVoice";
import { formatFileSize, formatFileType } from "../../utils/fileUtil";
import Navbar from "../../components/Navbar";
import ThreadClosedMessage from "./components/ThreadClosedMessage";
import SupportAgentStatus from "./components/SupportAgentStatus";
import Loader from "./components/Loader";
import { useTranslation } from "../../contexts/useTranslation";
import { useCore } from "../../contexts/useCore";
import { useScrollAnchorOnResize } from "../../contexts/useScrollAnchorOnResize";
import { useReadingAnchor } from "./useReadingAnchor";
import { useCoreState } from "../../contexts/useCoreState";
import type { LastBotState } from "../../core/state";
import { Slot, Templated } from "../../templating";

interface ConversationPageProps {
  params: Record<string, string>;
  pageContext: PageContext | null;
  navigate: (_pageOptions: PageOptions) => void;
  isFullscreen: boolean;
}

const threadSelector = (state: LastBotState): LastBotState["thread"] => state.thread;
const typingSelector = (state: LastBotState): boolean => state.agents.typing;

const findInvitedAgent = (messageList: Message[]): User | null => {
  const agentInvitedMessage = [...messageList].reverse().find(
    (message: Message) => message.role === "event" && message.metadata?.event === "agent_invited"
  );
  return agentInvitedMessage?.sender ?? null;
};

const ConversationPage: FC<ConversationPageProps> = ({ params, pageContext, navigate, isFullscreen }) => {
  const { translate } = useTranslation();
  const { scrollRef, scrollToBottom, restoreReadingPosition, hasReadingAnchor } = useScroll();
  const core = useCore();
  const thread = useCoreState(core, threadSelector);
  const isAgentTyping = useCoreState(core, typingSelector);

  // UI-only state preserved here. Persistent thread state lives in the core.
  const [pendingFirstMessage, setPendingFirstMessage] = useState<Message | null>(null);
  const [isLoading, setIsLoading] = useState(pageContext === null);
  const [isVoiceMode, setIsVoiceMode] = useState(false);

  const messageThreadId = thread?.id != null ? thread.id.toString() : (params.messageThreadId ?? "");
  const threadClosedAt = thread?.closedAt ?? null;
  const rating = thread?.rating ?? null;
  const assignedSupportAgent = thread?.assignedUser ?? null;
  const connected = thread?.connected ?? false;

  const displayMessages = useMemo(
    () => {
      const baseMessages = thread?.messages ?? [];
      return pendingFirstMessage && baseMessages.length === 0
        ? [pendingFirstMessage]
        : baseMessages;
    },
    [pendingFirstMessage, thread?.messages],
  );

  const invitedSupportAgent = useMemo(
    () => findInvitedAgent(displayMessages),
    [displayMessages],
  );

  const handleStartVoiceMode = useCallback(() => {
    setIsVoiceMode(true);
  }, []);

  const handleExitVoiceMode = useCallback(() => {
    setIsVoiceMode(false);
  }, []);

  // Bootstrap: either open an existing thread (by id) or send the first message that creates one.
  useEffect(() => {
    let cancelled = false;

    const openExistingThread = async (): Promise<void> => {
      const id = parseInt(params.messageThreadId ?? "", 10);
      if (!Number.isFinite(id)) {
        navigate({ page: { type: "landing" } });
        return;
      }
      try {
        await core.openThread(id);
        if (cancelled) return;
        setIsLoading(false);
        setTimeout(scrollToBottom, 0);
      } catch (err) {
        if (cancelled) return;
        const status = (err as { cause?: { status?: number } })?.cause?.status;
        if (status === 404) {
          navigate({ page: { type: "landing" } });
        } else if (status !== 499) {
          navigate({ page: { type: "errorPage" } });
        }
      }
    };

    const createThread = async (ctx: PageContext): Promise<void> => {
      // Display a temporary user message immediately while the backend processes the request.
      setPendingFirstMessage({
        id: -1,
        updated_at: new Date().toISOString(),
        role: "user",
        is_finished: true,
        contents: ctx.message,
        metadata: null,
        attachment: ctx.fileUpload?.file
          ? {
              filename: ctx.fileUpload.file.name,
              url: "#",
              type: formatFileType(ctx.fileUpload.file.type),
              size: formatFileSize(ctx.fileUpload.file.size),
            }
          : null,
        sender: null,
      });

      try {
        await core.startNewThread(ctx.message, { attachmentId: ctx.fileUpload?.id ?? undefined });
        if (cancelled) return;
        setPendingFirstMessage(null);
        // After core creates the thread it emits thread:opened; useThreadPagePersistence (App) persists the page state.
        setTimeout(scrollToBottom, 0);
      } catch (err) {
        if (cancelled) return;
        setPendingFirstMessage(null);
        const status = (err as { cause?: { status?: number } })?.cause?.status;
        if (status !== 499) {
          navigate({ page: { type: "errorPage" } });
        }
      }
    };

    if (pageContext) {
      void createThread(pageContext);
    } else {
      void openExistingThread();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Route channel errors to the right page.
  useEffect(() => {
    const handleError = ({ code, details }: { code: string; details?: unknown }): void => {
      if (code !== "thread.message-channel-error") return;
      const status = (details as { status?: number } | undefined)?.status;
      if (status === 404) {
        navigate({ page: { type: "landing" } });
      } else {
        navigate({ page: { type: "errorPage" } });
      }
    };
    core.on("error", handleError);
    return () => core.off("error", handleError);
  }, [core, navigate]);

  // Scroll on new incoming messages once we are past initial load: the
  // reader's question to the top when a reply starts, the bottom otherwise.
  useReadingAnchor(displayMessages, !isLoading, thread?.id ?? null);

  useScrollAnchorOnResize(core, scrollRef, restoreReadingPosition, hasReadingAnchor);

  return (
    <Templated id="lastbot-conversation-page">
      <>
        <Slot name="navbar">
          <Navbar isFullscreen={isFullscreen}>
            <button
              type="button"
              onClick={() => navigate({ page: { type: "customerInquiry" } })}
              className="-ml-1 text-lastbot-icon-muted cursor-pointer"
            >
              <ArrowLeftIcon />
            </button>
          </Navbar>
        </Slot>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div
              ref={scrollRef}
              className="flex-1 flex flex-col p-4 overflow-y-auto"
            >
              <Slot name="message-list">
                <ChatMessageList
                  messageThreadId={messageThreadId}
                  messages={displayMessages}
                  threadClosedAt={threadClosedAt}
                  rating={rating}
                />
              </Slot>
            </div>
            {threadClosedAt ? (
              <Slot name="conversation-footer">
                <ThreadClosedMessage onStartNewConversation={() => navigate({ page: { type: "landing" } })} />
              </Slot>
            ) : (
              <div className="px-4 pb-4 md:max-w-[var(--lastbot-content-max-width)] md:w-full md:self-center">
                <Slot name="composer-text">
                  <>
                    <div className={`mb-2 pl-4 text-sm text-lastbot-icon-muted transition-opacity duration-300 ${isAgentTyping ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                      {translate("person_is_typing", { person_name: assignedSupportAgent?.first_name || translate("support_agent") })}
                    </div>
                    {isVoiceMode ? (
                      <ChatFormVoice onExitVoiceMode={handleExitVoiceMode} />
                    ) : (
                      <ChatFormText
                        connected={connected}
                        onStartVoiceMode={handleStartVoiceMode}
                      />
                    )}
                  </>
                </Slot>
                <Slot name="conversation-footer">
                  {!assignedSupportAgent ? <SupportAgentStatus invitedSupportAgent={invitedSupportAgent} /> : <></>}
                </Slot>
              </div>
            )}
          </>
        )}
      </>
    </Templated>
  );
};

export default ConversationPage;
